<!--
  Created 17.06.2023
  Google Maps modal for displaying custom markers.
  Uses Vue 3 Google Maps package as base:
  https://vue-map.netlify.app/
-->
<template>
  <div
    class="relative flex flex-col"
    :class="classes">
    <div
      class="relative basis-full">
      <GMapMap
        :center="{ lat: centerLats, lng: centerLngs }"
        :zoom="zoomCenter"
        map-type-id="roadmap"
        :options="{
          zoomControl: true,
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: false,
        }"
        style="position: absolute; width: 100%; height: 100%;">
        <GMapMarker
          v-for="(marker, index) in markers"
          :key="index"
          :position="marker.position"
          :clickable="true"
          :draggable="false"
          :label="marker.title?.split(' ').map(x => x[0]).join('').slice(0, 2)"
          :title="marker.title"
          @click="emit('update:selectedMarker', marker)" />
        <GMapMarker
          v-if="newMarker?.title"
          ref="gMapNewMarker"
          :key="newMarker.id"
          :position="newMarker.position"
          :clickable="true"
          :draggable="true"
          :label="newMarker.title?.split(' ').map(x => x[0]).join('').slice(0, 2)"
          :title="newMarker.title"
          @click="emit('update:newMarker', newMarker)" />
      </GMapMap>
    </div>
  </div>
</template>

<script setup>
/* external */
import {
  computed, onMounted, ref, watch,
} from 'vue';

/* Define props */
const props = defineProps({
  classes: {
    type: String,
    required: false,
    default: 'relative h-full w-full',
  },
  location: {
    type: Object,
    required: true,
    default: () => ({
      lat: 59.743388991957616,
      lng: 10.205983950232687,
    }),
  },
  markers: {
    type: Array,
    required: false,
    default: () => ([
      {
        title: '',
        position: {
          lat: 0,
          lng: 0,
        },
        selected: false,
      },
    ]),
  },
  newMarker: {
    type: Object,
    required: false,
    default: () => ({
      title: '',
      position: {
        lat: 0,
        lng: 0,
      },
      selected: false,
    }),
  },
  filterSelected: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(['update:selectedMarker', 'update:newMarker']);
const gMapNewMarker = ref(null);

/**
 * Map handling of markers
 */

/* Map related data setup */
const markers = computed(() => {
  if (props.filterSelected) {
    const selectedMarker = props.markers.filter((marker) => marker.selected);
    if (selectedMarker.length > 0) {
      return selectedMarker;
    } else {
      return props.markers.filter((marker) => marker.within50km);
    }
  }

  return props.markers;
});

const centerLats = computed(() => {
  const lats = markers.value.map((marker) => marker.position.lat);
  const latMax = Math.max(...lats);
  const latMin = Math.min(...lats);

  return (latMin + latMax) / 2 || props.location.coordinates[1];
});

const centerLngs = computed(() => {
  const lngs = markers.value.map((marker) => marker.position.lng);
  const lngMax = Math.max(...lngs);
  const lngMin = Math.min(...lngs);

  return (lngMin + lngMax) / 2 || props.location.coordinates[0];
});

const zoomCenter = computed(() => {
  if (markers.value.length === 0) {
    return 13;
  }
  if (markers.value.length === 1) {
    return 16;
  }
  const lngs = markers.value.map((marker) => marker.position.lng);
  const lats = markers.value.map((marker) => marker.position.lat);
  const lngMax = Math.max(...lngs);
  const lngMin = Math.min(...lngs);
  const latMax = Math.max(...lats);
  const latMin = Math.min(...lats);
  const max = Math.max(latMax - latMin, lngMax - lngMin);

  return ((2.7 - (markers.value.length / 500)) - (max * 2)) * 5;
});

const newMarker = computed(() => props.newMarker);

watch(gMapNewMarker, () => {
  if (gMapNewMarker.value !== null) {
    gMapNewMarker.value.$markerPromise.then((markerObject) => {
      markerObject.addListener('dragend', () => {
        const position = markerObject.getPosition();
        emit('update:newMarker', {
          ...newMarker,
          position: {
            lat: position.lat(),
            lng: position.lng(),
          },
        });
      });
    });
  }
});

</script>
